import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AdminGarageTabs } from "@/components/layout/AdminGarageTabs";
import { PageHeader, LoadingState, ErrorState, Field, FormError, SubmitButton } from "@/components/common";
import { GaragesTable } from "@/pages/admin/AdminDashboard";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { TIMEZONES, CURRENCIES } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { t } from "@/i18n";

import { slugify } from "@/components/signup/SignupSteps";

export function SlugStatus({ slug, path }) {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!slug) return setState(null);
    const id = setTimeout(() => {
      api.get(path, { params: { slug } })
        .then(({ data }) => setState(data.available ? { ok: true } : { ok: false, msg: t("slug.taken") }))
        .catch((e) => setState({ ok: false, msg: errMsg(e) }));
    }, 350);
    return () => clearTimeout(id);
  }, [slug, path]);
  if (!state) return null;
  return (
    <p data-testid="slug-availability" className={`flex items-center gap-1.5 text-xs font-medium ${state.ok ? "text-emerald-700" : "text-red-600"}`}>
      {state.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
      {state.ok ? t("slug.available") : state.msg}
    </p>
  );
}

function CreateGarageDialog({ open, onOpenChange, plans, onCreated }) {
  const { me } = useAuth();
  const empty = { name: "", slug: "", timezone: "Australia/Perth", currency: "AUD", plan_id: "", subscription_status: "trial", trial_days: 30, manager_name: "", manager_email: "" };
  const [f, setF] = useState(empty);
  const [slugTouched, setSlugTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value, ...(k === "name" && !slugTouched ? { slug: slugify(e.target.value) } : {}) });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.plan_id) return setError(t("admin.choosePlan"));
    setBusy(true);
    setError("");
    try {
      const payload = { ...f, trial_days: Number(f.trial_days), manager_email: f.manager_email || null };
      const { data } = await api.post("/admin/garages", payload);
      toast.success(f.manager_email ? t(me.demo_mode ? "admin.createdInvitedDemo" : "admin.createdInvitedNoProvider") : t("admin.created"));
      setF(empty);
      setSlugTouched(false);
      onCreated(data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="create-garage-dialog">
        <DialogHeader>
          <DialogTitle>{t("admin.createGarage")}</DialogTitle>
          <DialogDescription>{t("admin.createGarageHint")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <FormError message={error} />
          <Field label={t("garage.name")} htmlFor="cg-name">
            <Input id="cg-name" required value={f.name} onChange={set("name")} data-testid="create-garage-name-input" />
          </Field>
          <Field label={t("slug.label")} htmlFor="cg-slug" hint={`${window.location.origin}/g/${f.slug || "…"}`}>
            <Input id="cg-slug" required value={f.slug} onChange={(e) => { setSlugTouched(true); setF({ ...f, slug: e.target.value.toLowerCase() }); }} data-testid="create-garage-slug-input" />
            <SlugStatus slug={f.slug} path="/admin/slug-available" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("garage.timezone")}>
              <Select value={f.timezone} onValueChange={(v) => setF({ ...f, timezone: v })}>
                <SelectTrigger data-testid="create-garage-timezone-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("garage.currency")}>
              <Select value={f.currency} onValueChange={(v) => setF({ ...f, currency: v })}>
                <SelectTrigger data-testid="create-garage-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("admin.col.plan")}>
              <Select value={f.plan_id} onValueChange={(v) => setF({ ...f, plan_id: v })}>
                <SelectTrigger data-testid="create-garage-plan-select"><SelectValue placeholder={t("admin.choosePlan")} /></SelectTrigger>
                <SelectContent>{plans.filter((p) => p.active).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({t("admin.usersMax", { n: p.max_internal_users })})</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("admin.initialStatus")}>
              <Select value={f.subscription_status} onValueChange={(v) => setF({ ...f, subscription_status: v })}>
                <SelectTrigger data-testid="create-garage-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">{t("status.trial")}</SelectItem>
                  <SelectItem value="active">{t("status.active")}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="mb-3 text-sm font-semibold text-slate-800">{t("admin.managerInvite")}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("invite.fullName")} htmlFor="cg-mname"><Input id="cg-mname" value={f.manager_name} onChange={set("manager_name")} data-testid="create-garage-manager-name-input" /></Field>
              <Field label={t("auth.email")} htmlFor="cg-memail"><Input id="cg-memail" type="email" value={f.manager_email} onChange={set("manager_email")} data-testid="create-garage-manager-email-input" /></Field>
            </div>
          </div>
          <div className="gf-modal-footer">
          <SubmitButton busy={busy} className="w-full" data-testid="create-garage-submit-button">{t("admin.createGarage")}</SubmitButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminGarages() {
  const navigate = useNavigate();
  const garages = useFetch("/admin/garages");
  const plans = useFetch("/admin/plans");
  const [open, setOpen] = useState(false);
  return (
    <div>
      <AdminGarageTabs />
      <PageHeader
        eyebrow={t("admin.area")}
        title={t("admin.nav.garages")}
        subtitle={t("admin.garagesSubtitle")}
        actions={<Button onClick={() => setOpen(true)} data-testid="open-create-garage-button"><Plus className="mr-2 h-4 w-4" /> {t("admin.createGarage")}</Button>}
      />
      {garages.loading && !garages.data && <LoadingState />}
      {garages.error && <ErrorState message={garages.error} onRetry={garages.reload} />}
      {garages.data && <GaragesTable garages={garages.data} />}
      <CreateGarageDialog open={open} onOpenChange={setOpen} plans={plans.data || []} onCreated={(d) => { setOpen(false); navigate(`/admin/garages/${d.id}`); }} />
    </div>
  );
}
